import { SITE_NAME, SITE_URL, SEO_CONFIG } from "@/constants/seo";

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  structuredData?: Record<string, any> | Record<string, any>[];
}

export const generatePageTitle = (title?: string): string => {
  if (!title) return SEO_CONFIG.defaultTitle;
  return SEO_CONFIG.titleTemplate.replace("%s", title);
};

export const generateCanonicalUrl = (path: string): string => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
};

export const generateLocalBusinessSchema = (business: {
  name: string;
  description: string;
  address?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  image?: string;
  url?: string;
  type: "GymAndFitnessCenter" | "SportsEquipmentProvider" | "Event";
  rating?: number;
  reviewCount?: number;
  priceRange?: string;
}) => {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": business.type === "Event" ? "Event" : business.type,
    name: business.name,
    description: business.description,
  };

  if (business.image) {
    schema.image = business.image;
  }

  if (business.url) {
    schema.url = business.url;
  }

  if (business.type !== "Event") {
    if (business.address || business.city) {
      schema.address = {
        "@type": "PostalAddress",
        streetAddress: business.address || "",
        addressLocality: business.city || "",
        postalCode: business.postalCode || "",
        addressCountry: "ES",
      };
    }

    if (business.phone) {
      schema.telephone = business.phone;
    }

    if (business.rating && business.rating > 0) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: business.rating.toString(),
        reviewCount: business.reviewCount || 1, // Default to 1 if not provided
        bestRating: "5",
        worstRating: "1",
      };
    }

    if (business.priceRange) {
      schema.priceRange = business.priceRange;
    }
  }

  return schema;
};

export const generateBreadcrumbSchema = (
  items: Array<{ name: string; url: string }>
) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

export const generateProductSchema = (product: {
  name: string;
  description: string;
  image: string;
  price: number;
  currency?: string;
  brand?: string;
  sku?: string;
  rating?: number;
  reviewCount?: number;
}) => {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: product.image,
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: product.currency || "EUR",
      availability: "https://schema.org/InStock",
    },
  };

  if (product.brand) {
    schema.brand = {
      "@type": "Brand",
      name: product.brand,
    };
  }

  if (product.sku) {
    schema.sku = product.sku;
  }

  if (product.rating && product.reviewCount) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: product.rating,
      reviewCount: product.reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  return schema;
};

export const generateEventSchema = (event: {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location: {
    name: string;
    address?: string;
    city?: string;
  };
  image?: string;
  price?: number;
  organizer?: string;
  url?: string;
}) => {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.name,
    description: event.description,
    startDate: event.startDate,
    location: {
      "@type": "Place",
      name: event.location.name,
    },
  };

  if (event.endDate) {
    schema.endDate = event.endDate;
  }

  if (event.location.address || event.location.city) {
    schema.location.address = {
      "@type": "PostalAddress",
      streetAddress: event.location.address || "",
      addressLocality: event.location.city || "",
      addressCountry: "ES",
    };
  }

  if (event.image) {
    schema.image = event.image;
  }

  if (event.price !== undefined) {
    schema.offers = {
      "@type": "Offer",
      price: event.price,
      priceCurrency: "EUR",
      url: event.url || `${SITE_URL}/events`,
      validFrom: event.startDate,
    };
  }

  if (event.organizer) {
    schema.organizer = {
      "@type": "Organization",
      name: event.organizer,
    };
  }

  return schema;
};

export const cleanText = (text: string): string => {
  return text.replace(/\s+/g, " ").trim();
};

export const truncateDescription = (text: string, maxLength: number = 160): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + "...";
};
// Re-export generateSlug from centralized formatters for backward compatibility
export { generateSlug } from "@/utils/formatters";

export const generateArticleSchema = (article: {
  title: string;
  description: string;
  image?: string;
  author?: string;
  authorUrl?: string;
  datePublished: string;
  dateModified?: string;
  url: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    image: article.image || `${SITE_URL}/og-image.png`,
    author: {
      "@type": "Organization",
      name: article.author || SITE_NAME,
      url: article.authorUrl || SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/favicon.webp`,
      },
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.url,
    },
  };
};

export const generateNewsArticleSchema = (article: {
  title: string;
  description: string;
  image?: string;
  author?: string;
  authorUrl?: string;
  datePublished: string;
  dateModified?: string;
  url: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.description,
    image: article.image ? [article.image] : [`${SITE_URL}/og-image.png`],
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      "@type": "Organization",
      name: article.author || "Equipo Ekselencia",
      url: article.authorUrl || SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/favicon.webp`,
        width: 600,
        height: 60,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": article.url,
    },
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

export const generateVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  contentUrl?: string;
  embedUrl?: string;
  duration?: string;
}) => {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
  };

  if (video.contentUrl) {
    schema.contentUrl = video.contentUrl;
  }

  if (video.embedUrl) {
    schema.embedUrl = video.embedUrl;
  }

  if (video.duration) {
    schema.duration = video.duration;
  }

  return schema;
};

export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.webp`,
    description: "El directorio deportivo y fitness más completo de España",
    address: {
      "@type": "PostalAddress",
      addressCountry: "ES",
    },
    sameAs: [],
  };
};
